import { React, useState, useEffect } from "react";
import "./Cotizador.css";
import AppWrapper from "../AppWrapper";
import { useNavigate } from "react-router-dom";
import {
  Card,
  FormLayout,
  Page,
  Select,
  TextField,
  Text,
  BlockStack,
  InlineGrid,
  Divider,
  Modal,
  Button,
  Toast,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { hoursOfDay } from "../../utility/GlobalVariables";
import {
  calculateReservationTotal,
  getPriceLists,
  getReservationTime,
} from "../../utility/ReservationUtilities";
import { formatPrice, formatDate } from "../../utility/funcs";
import moment from "moment";

const API_URL = "https://api.gomoverent.com/v2";
const API_TOKEN = "h8ISmsSIwTvfl8Tny2nr0dFh2nz3rmiCzBMjtCJOJqn7TF6Y4wnch7uI1FI3ArlZP0wfMsGkEuqNXUzaX9wYSL5bnn9CzB1DkWAMFQVCBWBhRwAXn29M96YYxcp1bmfN";

function Cotizador() {
  const [dates, setDates] = useState({
    from_date: moment().format("YYYY-MM-DD"),
    from_hour: "09:00",
    to_date: moment().add(1, "day").format("YYYY-MM-DD"),
    to_hour: "09:00",
  });
  const [segment, setSegment] = useState(0);
  const [selectedModel, setSelectedModel] = useState(undefined);
  const [models, setModels] = useState([]);
  const [license, setLicense] = useState("local");
  const [insurance, setInsurance] = useState("premium");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [priceLists, setPriceLists] = useState([]);
  const [priceListId, setPriceListId] = useState(null);
  const [quoteDetails, setQuoteDetails] = useState(null);

  useEffect(() => {
    getPriceLists().then((arr) => {
      setPriceLists(arr);
      setPriceListId((cur) => cur ?? arr[0]?.id ?? null);
    });
  }, []);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [overrides, setOverrides] = useState({
    dailyPrice: null,
    hoursExtra: null,
    deposit: null,
    deliveryFee: 0,
    returnFee: 0,
    deliveryAddress: "",
    returnAddress: "",
  });
  const [editModal, setEditModal] = useState({ open: false, key: "", title: "", value: "" });
  const [toast, setToast] = useState({ active: false, content: "", error: false });

  const handleDateChange = (value, id) => {
    setDates({ ...dates, [id]: value });
  };

  const segments = [
    { label: "Todos", value: 0 },
    { label: "Sedán", value: 1 },
    { label: "Carga", value: 2 },
    { label: "Pasajeros", value: 3 },
  ];

  const handleSetSegment = (value) => {
    let id = parseInt(value);
    setSegment(id);
    if (id === 0) {
      setSelectedModel(models[0]?.id);
    } else {
      let found = models.find((model) => model.category.id === id);
      if (found) setSelectedModel(found.id);
    }
  };

  const getModelOptions = () => {
    const filtered =
      segment === 0
        ? models
        : models.filter((model) => model.category.id === segment);
    return filtered.map((model) => ({
      label: `${model.brand.name} ${model.name}`,
      value: model.id,
    }));
  };

  const handleSetModel = (value) => {
    setSelectedModel(parseInt(value));
  };

  useEffect(() => {
    async function fetchModels() {
      const { data, error } = await supabase
        .from("car_models")
        .select("*, brand(*), category(*)");
      if (error) {
        console.error(error.message);
        return;
      }
      data.sort((a, b) => a.name.localeCompare(b.name, "es"));
      setModels(data);
      if (data.length > 0) setSelectedModel(data[0].id);
    }
    fetchModels();
  }, []);

  // A manual daily price only applies to the parameters it was set for —
  // drop it when any of them change so the calculated rate takes over again.
  useEffect(() => {
    setOverrides((cur) => (cur.dailyPrice === null ? cur : { ...cur, dailyPrice: null }));
  }, [selectedModel, insurance, license, paymentMethod, priceListId]);

  // Calculate quote when inputs change
  useEffect(() => {
    if (!selectedModel || !dates.from_date || !dates.to_date) {
      setQuoteDetails(null);
      return;
    }

    const calculate = async () => {
      const time = getReservationTime(
        dates.from_date,
        dates.from_hour,
        dates.to_date,
        dates.to_hour
      );

      if (!time.days && !time.hours) {
        setQuoteDetails(null);
        return;
      }

      const result = await calculateReservationTotal(
        time,
        selectedModel,
        paymentMethod,
        insurance,
        license,
        priceListId
      );
      setQuoteDetails(result);
    };

    calculate();
  }, [
    dates.from_date,
    dates.from_hour,
    dates.to_date,
    dates.to_hour,
    selectedModel,
    insurance,
    license,
    paymentMethod,
    priceListId,
  ]);

  // Compute effective values with overrides
  const effectiveDaily = overrides.dailyPrice !== null ? overrides.dailyPrice : (quoteDetails?.dailyPrice || 0);
  const effectiveHoursExtra = overrides.hoursExtra !== null ? overrides.hoursExtra : (quoteDetails?.hoursExtra || 0);
  const effectiveDeposit = overrides.deposit !== null ? overrides.deposit : (quoteDetails?.deposit || 0);
  const effectiveDays = quoteDetails?.time?.days || 0;
  const effectiveRent = (effectiveDaily * effectiveDays) + effectiveHoursExtra;
  const effectiveTotal = effectiveRent + overrides.deliveryFee + overrides.returnFee;
  const effectiveGrandTotal = effectiveTotal + effectiveDeposit;

  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  const buildQuotePayload = () => {
    if (!quoteDetails || !selectedModel) return null;
    const model = models.find((m) => m.id === selectedModel);
    return {
      insurance,
      license,
      payment: paymentMethod,
      place: "",
      contact: { name: customerName, email: customerEmail, phone: "" },
      car: { brand: model?.brand?.name || "", name: model?.name || "", category: model?.category?.name || "", image_url: model?.image_url || "" },
      fromDate: `${dates.from_date} ${dates.from_hour}`,
      toDate: `${dates.to_date} ${dates.to_hour}`,
      prices: {
        dailyPrice: effectiveDaily,
        hoursExtra: effectiveHoursExtra,
        deposit: effectiveDeposit,
        delivery: overrides.deliveryFee + overrides.returnFee,
        totalToPay: effectiveTotal,
        total: effectiveGrandTotal,
        rentPrice: effectiveRent,
      },
      rentTime: quoteDetails.time,
      source: "admin",
      skipNotification: true,
    };
  };

  const downloadPdf = async () => {
    const payload = buildQuotePayload();
    if (!payload) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/quote/send.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success && data.data?.pdfUrl) {
        window.open(data.data.pdfUrl, "_blank");
      } else {
        setToast({ active: true, content: data.error || "No se pudo generar el PDF", error: true });
      }
    } catch (err) {
      setToast({ active: true, content: err.message, error: true });
    } finally {
      setSending(false);
    }
  };

  const sendEmail = async () => {
    if (!customerEmail) {
      setToast({ active: true, content: "Ingresa un correo electrónico", error: true });
      return;
    }
    const payload = buildQuotePayload();
    if (!payload) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/quote/send.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success && data.data?.emailSent) {
        setToast({ active: true, content: "Cotización enviada por correo", error: false });
      } else {
        setToast({ active: true, content: data.data?.emailError || data.error || "No se pudo enviar", error: true });
      }
    } catch (err) {
      setToast({ active: true, content: err.message, error: true });
    } finally {
      setSending(false);
    }
  };

  const saveAsQuotation = async () => {
    if (!quoteDetails || !selectedModel) return;
    const effectiveTotals = { ...quoteDetails, dailyPrice: effectiveDaily, hoursExtra: effectiveHoursExtra, deposit: effectiveDeposit, totalRent: effectiveRent };
    const { data, error } = await supabase.from("reservations").insert({
      type: "rental",
      status: "quotation",
      date_range: `[${dates.from_date} ${dates.from_hour}, ${dates.to_date} ${dates.to_hour}]`,
      rent_time: quoteDetails.time,
      totals: effectiveTotals,
      total_price: effectiveTotal,
      balance: effectiveTotal,
      deposit: effectiveDeposit,
      insurance: insurance,
      price_list_id: priceListId,
      delivery: overrides.deliveryFee > 0 ? { address: overrides.deliveryAddress || "Domicilio", type: "domicilio", fee: overrides.deliveryFee } : { address: "Oficina Central - Colón", type: "sucursal", fee: 0 },
      return: overrides.returnFee > 0 ? { address: overrides.returnAddress || "Domicilio", type: "domicilio", fee: overrides.returnFee } : { address: "Oficina Central - Colón", type: "sucursal", fee: 0 },
      notes: customerName ? `Cotización para: ${customerName}${customerEmail ? ` (${customerEmail})` : ""}` : null,
    }).select("id").single();

    if (error) {
      setToast({ active: true, content: error.message, error: true });
      return;
    }
    navigate(`/reservation?id=${data.id}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    if (!quoteDetails) return;
    const model = models.find((m) => m.id === selectedModel);
    const text = [
      `*Cotización GoMove*`,
      ``,
      customerName ? `Cliente: ${customerName}` : null,
      `Vehículo: ${model?.brand?.name || ""} ${model?.name || ""}`,
      `Entrega: ${dates.from_date} ${dates.from_hour}`,
      `Devolución: ${dates.to_date} ${dates.to_hour}`,
      `Duración: ${effectiveDays} días${quoteDetails.time.hours ? ` y ${quoteDetails.time.hours} horas` : ""}`,
      ``,
      `Tarifa Diaria: ${formatPrice(effectiveDaily)}`,
      effectiveHoursExtra > 0 ? `Horas Extra: ${formatPrice(effectiveHoursExtra)}` : null,
      overrides.deliveryFee > 0 ? `Entrega: ${formatPrice(overrides.deliveryFee)}` : null,
      overrides.returnFee > 0 ? `Recolección: ${formatPrice(overrides.returnFee)}` : null,
      `*Total Renta: ${formatPrice(effectiveTotal)}*`,
      `Depósito: ${formatPrice(effectiveDeposit)}`,
      `*Total: ${formatPrice(effectiveGrandTotal)}*`,
    ]
      .filter(Boolean)
      .join("\n");

    navigator.clipboard.writeText(text).then(() => {
      setToast({ active: true, content: "Cotización copiada al portapapeles", error: false });
    });
  };

  return (
    <AppWrapper>
      <Page
        title="Cotizador"
        primaryAction={{
          content: sending ? "Generando..." : "Descargar PDF",
          onAction: downloadPdf,
          disabled: !quoteDetails || sending,
          loading: sending,
        }}
        secondaryActions={[
          {
            content: "Copiar WhatsApp",
            onAction: handleShare,
            disabled: !quoteDetails,
          },
        ]}
      >
        <InlineGrid columns="2fr 1fr" gap="400">
          <FormLayout>
          <FormLayout.Group condensed>
            <TextField
              label="A nombre de"
              value={customerName}
              onChange={setCustomerName}
            />
            <TextField
              label="Correo"
              value={customerEmail}
              onChange={setCustomerEmail}
              type="email"
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <TextField
              label="Entrega"
              type="date"
              value={dates.from_date}
              onChange={handleDateChange}
              id="from_date"
            />
            <Select
              label="‎ "
              options={hoursOfDay}
              value={dates.from_hour}
              onChange={handleDateChange}
              id="from_hour"
            />
            <TextField
              label="Devolución"
              type="date"
              value={dates.to_date}
              min={dates.from_date}
              onChange={handleDateChange}
              id="to_date"
            />
            <Select
              label="‎ "
              options={hoursOfDay}
              value={dates.to_hour}
              onChange={handleDateChange}
              id="to_hour"
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <Select
              label="Segmento"
              value={segment}
              options={segments}
              onChange={handleSetSegment}
            />
            <Select
              label="Modelo"
              value={selectedModel}
              options={getModelOptions()}
              onChange={handleSetModel}
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <Select
              label="Licencia"
              value={license}
              options={[
                { label: "Local", value: "local" },
                { label: "Foránea", value: "foreign" },
              ]}
              onChange={(value) => {
                setLicense(value);
                if (value === "foreign") {
                  setPaymentMethod("card");
                  setInsurance("premium");
                }
              }}
            />
            <Select
              label="Pago"
              value={paymentMethod}
              options={[
                { label: "Tarjeta de Crédito", value: "card" },
                { label: "Efectivo", value: "cash" },
              ]}
              onChange={setPaymentMethod}
              disabled={license === "foreign"}
            />
            <Select
              label="Seguro"
              value={insurance}
              options={[
                { value: "premium", label: "Premium 0%" },
                { value: "standard", label: "Standard 0%" },
                { value: "base", label: "Básico 10%" },
              ]}
              onChange={setInsurance}
              disabled={license === "foreign"}
            />
            {priceLists.length > 1 && (
              <Select
                label="Lista de precios"
                value={priceListId ? String(priceListId) : ""}
                options={priceLists.map((l) => ({
                  value: String(l.id),
                  label: `${l.title}${l.is_public ? " (Pública)" : ""}`,
                }))}
                onChange={(v) => setPriceListId(parseInt(v))}
              />
            )}
          </FormLayout.Group>

          </FormLayout>
          <BlockStack gap="300">
          {/* Summary */}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Resumen</Text>
              {quoteDetails ? (
                <>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Entrega</Text>
                    <Text variant="bodyMd" tone="subdued">
                      {formatDate(dates.from_date)} {dates.from_hour}
                    </Text>
                  </InlineGrid>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Devolución</Text>
                    <Text variant="bodyMd" tone="subdued">
                      {formatDate(dates.to_date)} {dates.to_hour}
                    </Text>
                  </InlineGrid>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Duración</Text>
                    <Text variant="bodyMd" tone="subdued">
                      {effectiveDays} días
                      {quoteDetails.time.hours
                        ? ` y ${quoteDetails.time.hours} horas`
                        : ""}
                    </Text>
                  </InlineGrid>
                  <Divider />
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Tarifa Diaria</Text>
                    <Button variant="plain" onClick={() => setEditModal({ open: true, key: "dailyPrice", title: "Tarifa Diaria", value: String(effectiveDaily) })}>
                      {formatPrice(effectiveDaily)}
                    </Button>
                  </InlineGrid>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Horas Extra</Text>
                    <Button variant="plain" onClick={() => setEditModal({ open: true, key: "hoursExtra", title: "Horas Extra", value: String(effectiveHoursExtra) })}>
                      {formatPrice(effectiveHoursExtra)}
                    </Button>
                  </InlineGrid>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Entrega a domicilio</Text>
                    <Button variant="plain" onClick={() => setEditModal({ open: true, key: "deliveryFee", title: "Entrega a domicilio", value: String(overrides.deliveryFee) })}>
                      {formatPrice(overrides.deliveryFee)}
                    </Button>
                  </InlineGrid>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Recolección</Text>
                    <Button variant="plain" onClick={() => setEditModal({ open: true, key: "returnFee", title: "Recolección", value: String(overrides.returnFee) })}>
                      {formatPrice(overrides.returnFee)}
                    </Button>
                  </InlineGrid>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd" fontWeight="bold">Total Renta</Text>
                    <Text variant="bodyMd" fontWeight="bold">{formatPrice(effectiveTotal)}</Text>
                  </InlineGrid>
                  <InlineGrid columns="1fr auto">
                    <Text variant="bodyMd">Depósito</Text>
                    <Button variant="plain" onClick={() => setEditModal({ open: true, key: "deposit", title: "Depósito", value: String(effectiveDeposit) })}>
                      {formatPrice(effectiveDeposit)}
                    </Button>
                  </InlineGrid>
                  <Divider />
                  <InlineGrid columns="1fr auto">
                    <Text variant="headingSm">Total (Renta + Depósito)</Text>
                    <Text variant="headingSm">{formatPrice(effectiveGrandTotal)}</Text>
                  </InlineGrid>
                </>
              ) : (
                <Text tone="subdued">
                  Selecciona fechas y modelo para ver la cotización
                </Text>
              )}
            </BlockStack>
          </Card>

          <Modal
            open={editModal.open}
            onClose={() => setEditModal({ ...editModal, open: false })}
            title={editModal.title}
            primaryAction={{
              content: "Guardar",
              onAction: () => {
                const val = parseInt(editModal.value) || 0;
                setOverrides({ ...overrides, [editModal.key]: val });
                setEditModal({ ...editModal, open: false });
              },
            }}
            secondaryActions={[{ content: "Cancelar", onAction: () => setEditModal({ ...editModal, open: false }) }]}
          >
            <Modal.Section>
              <TextField
                label={editModal.title}
                value={editModal.value}
                onChange={(val) => setEditModal({ ...editModal, value: val })}
                type="number"
                prefix="$"
                autoComplete="off"
                focused
              />
            </Modal.Section>
          </Modal>
          </BlockStack>
        </InlineGrid>
        {toast.active && (
          <Toast
            content={toast.content}
            error={toast.error}
            onDismiss={() => setToast({ ...toast, active: false })}
            duration={3000}
          />
        )}
      </Page>
    </AppWrapper>
  );
}

export default Cotizador;
